import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react'
import { Column, Row } from 'react-table'
import { ILocationFilters } from '../main/TechnicianScorecardMain'
import { UserContext } from '../../base/components/base-container/BaseContainer'
import hasPermission from '../../global/utils/user/has-permission'
import { PERMISSIONS } from '../../global/constants/permissions'
import fetchDataAndSetTable from '../../global/components/table/helpers/fetchDataAndSetTable'
import { ITechnicianScorecard } from '../../../../app/entities/TechnicianScorecard'
import { handleColumnFilterUpdate } from '../../global/components/table/helpers/multiColumnFilters'
import BasicFilter from '../../global/components/table/helpers/BasicFilter'
import NoFilter from '../../global/components/table/helpers/NoFilter'
import { getElapsedTime } from '../../global/utils/date/dates'
import Table from '../../global/components/table/Table'

function formatNumberForDisplay(
  number: number | undefined | null,
  totalInstallations: number,
  decimalPlaces = 0,
): string {
  const isNan = !number || (!!number && isNaN(number))
  const hasNoInstallations =
    totalInstallations === 0 || isNaN(totalInstallations)
  if (isNan || hasNoInstallations) return '-'
  return number.toFixed(decimalPlaces)
}

interface ITechnicianScorecardTableProps {
  locationFilters: ILocationFilters
  months: string[]
  year: number
  setIsOpen: Dispatch<SetStateAction<boolean>>
  setTechnicianScorecard: Dispatch<SetStateAction<ITechnicianScorecard | null>>
}

function TechnicianScorecardsTable(props: ITechnicianScorecardTableProps) {
  const { user } = useContext(UserContext)
  const { locationFilters, months, year, setIsOpen, setTechnicianScorecard } =
    props
  const { locationObjectId } = locationFilters

  const [tableState, setTableState] = useState({
    data: [] as ITechnicianScorecard[],
    isLoading: false,
    pageSize: 100,
    offSet: 0,
    count: 0,
  })
  const [columnFilters, setColumnFilters] = useState({})

  const isTechnicianScorecardDisabled = !hasPermission(user).allow(
    PERMISSIONS.TECHNICIAN_SCORECARDS.CATEGORY,
    PERMISSIONS.TECHNICIAN_SCORECARDS.ENTRIES.TECHNICIAN_SCORECARDS.NAME,
  )

  function handleClick(row: Row): void {
    if (isTechnicianScorecardDisabled) return
    setIsOpen(true)
    setTechnicianScorecard(row.original as ITechnicianScorecard)
  }

  const technicianScorecardTableEndpoind = `${process.env.REACT_APP_ORIGIN}/technicians/technician-scorecards`
  useEffect(() => {
    fetchDataAndSetTable<ITechnicianScorecard>({
      endpoint: technicianScorecardTableEndpoind,
      queries: [
        { locationObjectId },
        { ...columnFilters },
        { year: year.toString() },
        { months: months.toString() },
      ],
      setTableState,
    })
  }, [locationObjectId, columnFilters, months, year])

  function onColumnFilterUpdate(filtersUpdate: object) {
    handleColumnFilterUpdate(filtersUpdate, columnFilters, setColumnFilters)
  }

  const columns: Column<ITechnicianScorecard>[] = [
    {
      Header: 'Technician Name',
      accessor: ({ technician }) => technician?.name,
      Filter: BasicFilter({
        onEnter: (val: string) =>
          onColumnFilterUpdate({ technician: { name: val } }),
      }),
    },
    {
      Header: 'Tenure',
      accessor: 'technician',
      Filter: NoFilter(),
      Cell: ({ row: { original } }) => {
        const { technician } = original as ITechnicianScorecard
        const employmentStartDate = new Date(technician.employmentStartDate)
        const currentDate = new Date()
        const tenure = getElapsedTime(employmentStartDate, currentDate)
        const tenureString = technician.employmentStartDate
          ? `${tenure.years}y, ${tenure.months}m, ${tenure.days}d`
          : '-'
        return (
          <div style={{ display: 'flex', flexDirection: 'row', width: '75px' }}>
            {tenureString}
          </div>
        )
      },
    },
    {
      Header: '# Installations',
      accessor: 'totalInstallations',
      Filter: NoFilter(),
    },
    {
      Header: 'Overall Score',
      accessor: ({ overallScore, totalInstallations }) =>
        formatNumberForDisplay(overallScore, totalInstallations),
      Filter: NoFilter(),
    },
    {
      Header: 'Rank',
      accessor: ({ overallRank, totalInstallations }) => {
        if (totalInstallations === 0) return '-'
        return overallRank
      },
      Filter: NoFilter(),
    },
    {
      Header: 'Driver Safety',
      accessor: ({ driverSafety, totalInstallations }) =>
        formatNumberForDisplay(
          driverSafety?.driverSafetyScore,
          totalInstallations,
        ),
      Filter: NoFilter(),
    },
    {
      Header: 'Customer Rating',
      accessor: ({ customerRating, totalInstallations }) =>
        formatNumberForDisplay(
          customerRating?.customerRatingScore,
          totalInstallations,
        ),
      Filter: NoFilter(),
    },
    {
      Header: 'Installation Audit',
      accessor: ({ installationAudit, totalInstallations }) =>
        formatNumberForDisplay(
          installationAudit?.installationAuditScore,
          totalInstallations,
        ),
      Filter: NoFilter(),
    },
    {
      Header: '4-Tire Install',
      accessor: ({ fourTireInstallTime, totalInstallations }) =>
        formatNumberForDisplay(
          fourTireInstallTime?.fourTireInstallTimeScore,
          totalInstallations,
        ),
      Filter: NoFilter(),
    },
    {
      Header: 'Mileage Capture',
      accessor: ({ mileageCapture, totalInstallations }) =>
        formatNumberForDisplay(
          mileageCapture?.mileageCaptureScore,
          totalInstallations,
        ),
      Filter: NoFilter(),
    },
    {
      Header: 'Timeliness',
      accessor: ({ onTimeClockIn, totalInstallations }) =>
        formatNumberForDisplay(
          onTimeClockIn?.onTimeClockInScore,
          totalInstallations,
        ),
      Filter: NoFilter(),
    },
    {
      Header: 'Quality Score',
      accessor: ({ qualityScore, totalInstallations }) =>
        formatNumberForDisplay(qualityScore?.qualityScore, totalInstallations),
      Filter: NoFilter(),
    },
  ]

  return (
    <div style={{ width: '100%' }}>
      <Table
        columns={columns}
        data={tableState.data ?? []}
        isLoading={tableState.isLoading}
        onRowClick={handleClick}
        pagination={{ setTableState, tableState }}
        isTableTopperPresent={false}
      />
    </div>
  )
}

export default TechnicianScorecardsTable
